import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
  TableContainer,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Autocomplete,
} from "@mui/material";
import VantageListHead from "../../../../../component/ListHead/SignalList";
import {
  Tablecell,
  applySortFilter,
  getComparator,
} from "../../../../../component/Methods";
import Legend from "../../../Components/Legend";

const SwapRebalancedData = ({
  existingNewArray,
  tableHeader,
  setExistingNewArray = () => { },
}) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("ticker");
  const [editCell, setEditCell] = useState({ index: null, field: null });

  const styleOptions = [
    { value: "VWAP", label: "VWAP" },
    { value: "VWAP_OVER_DAY", label: "VWAP Over Day" },
    { value: "TWAP", label: "TWAP" },
    { value: "MOC", label: "MOC" },
    { value: "PARTICIPATE", label: "PARTICIPATE" },
  ];

  const handleRequestSort = (event, property) => {
    if (editCell.index !== null) {
      return;
    }
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    const filteredUsers = applySortFilter(
      existingNewArray,
      getComparator(order, orderBy)
    );
    setData(filteredUsers);
  }, [order, orderBy, existingNewArray]);

  const closingPositions = existingNewArray?.filter(
    (item) => item.signal === 0
  ).length;
  const newPositions = existingNewArray?.filter(
    (item) => item.position === 0 && item.signal !== 0
  ).length;

  const updateRowPreference = (index, field, value) => {
    const updatedArray = [...existingNewArray];
    updatedArray[index] = {
      ...updatedArray[index],
      [field]: value,
    };
    setExistingNewArray(updatedArray);
  };

  const getRowConditionalLogic = (row) => {
    const isTimeDisabled = ["VWAP_OVER_DAY", "MOC", "PARTICIPATE"].includes(
      row.style
    );
    const isParticipationDisabled = ["TWAP", "MOC"].includes(row.style);

    return {
      isTimeDisabled,
      isParticipationDisabled,
    };
  };

  const handleCellClick = (index, field) => {

    if (editCell.index === index && editCell.field === field) {
      return;
    }
    setEditCell({ index, field });
  };

  const handleCellBlur = () => {
    setTimeout(() => {
      setEditCell({ index: null, field: null });
    }, 100);
  };

  const getStyleDisplayValue = (style) => {
    switch (style) {
      case "VWAP_OVER_DAY":
        return "VWAP Over Day";
      case "VWAP":
        return "VWAP";
      case "TWAP":
        return "TWAP";
      case "MOC":
        return "MOC";
      case "PARTICIPATE":
        return "PARTICIPATE";
      default:
        return "VWAP Over Day";
    }
  };

  return (
    <>
      <Box
        sx={{
          border: `100px ${theme.palette.lightGrey.tableHeader}`,
          borderRadius: "4px",
          marginTop: "1px",
          width: "100%",
          marginRight: 1,
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: "18px", mb: 1 }}>
          <Box sx={{ width: "auto", maxWidth: "800px" }}>
            <Legend closingCount={closingPositions} newCount={newPositions} />
          </Box>
        </Typography>

        <TableContainer style={{ height: "450px", width: "100%" }}>
          <Table
            size="small"
            stickyHeader
            aria-label="a dense table"
            sx={{
              height: "max-content",
            }}
          >
            <VantageListHead
              order={order}
              orderBy={orderBy}
              headLabel={tableHeader}
              noStatus={true}
              customizeFont={12}
              rowCount={data.length}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {data?.map((row, index) => {
                const conditionalLogic = getRowConditionalLogic(row);
                const originalIndex = existingNewArray.findIndex(item =>
                  item.ticker === row.ticker &&
                  item.position === row.position &&
                  item.signal === row.signal
                );

                return (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor:
                        row.position === 0 && row.signal != 0
                          ? "rgba(76, 175, 80, 0.1)"
                          : row.signal === 0
                            ? "rgba(244, 67, 54, 0.1)"
                            : "inherit",
                      "&:hover": {
                        background: theme.palette.grey[200],
                        cursor: "pointer",
                      },
                      border: "none",
                    }}
                  >
                    <Tablecell
                      align="left"
                      style={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        width: "100px",
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.ticker?.toUpperCase() || ""}
                    </Tablecell>
                    <Tablecell
                      align="right"
                      style={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        width: "100px",
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.position === undefined || row?.position === 0
                        ? "-"
                        : row?.position < 0
                          ? `(${Math.abs(row?.position)?.toFixed(4)})`
                          : `${row?.position?.toFixed(4)}`}
                    </Tablecell>

                    <Tablecell
                      align="right"
                      style={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        width: "100px",
                        borderColor: theme.palette.divider,
                        color:
                          row?.signal > 0
                            ? theme.palette.success.main
                            : row?.signal == 0
                              ? theme.palette.text.primary
                              : theme.palette.error.main,
                      }}
                    >
                      {row?.signal !== undefined
                        ? row?.signal < 0
                          ? `(${Math.abs(row?.signal)?.toFixed(4)})`
                          : `${row?.signal?.toFixed(4)}`
                        : ""}
                    </Tablecell>

                    <Tablecell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        width: "100px",
                        color: row?.diff > 0 ? theme.palette.success.main : theme.palette.error.main,

                        borderColor: theme.palette.divider,
                      }}
                    >
                      {row?.diff === undefined || row?.diff === 0
                        ? "-"
                        : row?.diff < 0
                          ? (
                            <Typography
                              sx={{
                                color: theme.palette.error.main,
                              }}
                            >
                              ({Math.abs(row?.diff)?.toFixed(4)})
                            </Typography>
                          )
                          : (
                            <Typography
                              sx={{
                                color: theme.palette.success.main,
                              }}
                            >
                              {row?.diff?.toFixed(4)}
                            </Typography>
                          )}
                    </Tablecell>

                    <Tablecell
                      align="left"
                      sx={{
                        paddingLeft: "5px",
                        width: "150px",
                        minWidth: "150px",
                        maxWidth: "150px",
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCellClick(originalIndex, "style");
                      }}
                    >
                      {editCell.index === index &&
                        editCell.field === "style" ? (
                        <Autocomplete
                          options={styleOptions}
                          getOptionLabel={(option) => option.label}
                          value={
                            styleOptions.find(
                              (opt) =>
                                opt.value === (row.style || "VWAP_OVER_DAY")
                            ) || styleOptions[1]
                          }
                          onChange={(event, newValue) => {
                            updateRowPreference(
                              originalIndex,
                              "style",
                              newValue ? newValue.value : "VWAP_OVER_DAY"
                            );
                          }}
                          onBlur={handleCellBlur}
                          autoFocus
                          size="small"
                          disableClearable
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                style: {
                                  fontSize: "12px",
                                  paddingTop: "0px",
                                  paddingBottom: "0px",
                                  width: "120px",
                                },
                              }}
                            />
                          )}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              border: "none",
                            },
                          }}
                        />
                      ) : (
                        getStyleDisplayValue(row.style || "VWAP_OVER_DAY")
                      )}
                    </Tablecell>
                    <Tablecell
                      align="left"
                      sx={{ paddingLeft: "5px", color: theme.palette.text.primary, borderColor: theme.palette.divider }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCellClick(originalIndex, "startTime");
                      }}
                    >
                      {editCell.index === originalIndex &&
                        editCell.field === "startTime" ? (
                        <TextField
                          key={`startTime-${originalIndex}`}
                          value={row?.startTime || ""}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value === "" || /^[\d:]*$/.test(value)) {
                              updateRowPreference(
                                originalIndex,
                                "startTime",
                                value
                              );
                            }
                          }}
                          disabled={false}
                          onBlur={handleCellBlur}
                          size="small"
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            style: {
                              fontSize: "12px",
                              paddingTop: "0px",
                              paddingBottom: "0px",
                              width: "50px",
                            },
                          }}
                          autoFocus
                        />
                      ) : (
                        <span style={{
                          color: conditionalLogic.isTimeDisabled ? '#ccc' : 'inherit',
                          cursor: conditionalLogic.isTimeDisabled ? 'not-allowed' : 'pointer'
                        }}>
                          {row?.startTime || "3:00"}
                        </span>
                      )}
                    </Tablecell>
                    <Tablecell
                      align="left"
                      sx={{ paddingLeft: "5px", color: theme.palette.text.primary, borderColor: theme.palette.divider }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCellClick(originalIndex, "endTime");
                      }}
                    >
                      {editCell.index === originalIndex &&
                        editCell.field === "endTime" ? (
                        <TextField
                          key={`endTime-${originalIndex}`}
                          value={row?.endTime || ""}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value === "" || /^[\d:]*$/.test(value)) {
                              updateRowPreference(
                                originalIndex,
                                "endTime",
                                value
                              );
                            }
                          }}
                          disabled={false}
                          onBlur={handleCellBlur}
                          size="small"
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            style: {
                              fontSize: "12px",
                              paddingTop: "0px",
                              paddingBottom: "0px",
                              width: "50px",
                            },
                          }}
                          autoFocus
                        />
                      ) : (
                        <span style={{
                          color: conditionalLogic.isTimeDisabled ? '#ccc' : 'inherit',
                          cursor: conditionalLogic.isTimeDisabled ? 'not-allowed' : 'pointer'
                        }}>
                          {row?.endTime || "7:00"}
                        </span>
                      )}
                    </Tablecell>
                    <Tablecell
                      align="left"
                      sx={{ paddingLeft: "5px", color: theme.palette.text.primary, borderColor: theme.palette.divider }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCellClick(originalIndex, "participation");
                      }}
                    >
                      {editCell.index === originalIndex &&
                        editCell.field === "participation" ? (
                        <TextField
                          key={`participation-${originalIndex}`}
                          value={row?.participation || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d*(\.\d*)?$/.test(value)) {
                              updateRowPreference(
                                originalIndex,
                                "participation",
                                value
                              );
                            }
                          }}
                          onBlur={handleCellBlur}
                          size="small"
                          variant="standard"
                          disabled={false}
                          InputProps={{
                            disableUnderline: true,
                            style: {
                              fontSize: "12px",
                              paddingTop: "0px",
                              paddingBottom: "0px",
                              width: "100px",
                            },
                          }}
                          autoFocus
                        />
                      ) : (
                        <span style={{
                          color: conditionalLogic.isParticipationDisabled ? '#ccc' : 'inherit',
                          cursor: conditionalLogic.isParticipationDisabled ? 'not-allowed' : 'pointer'
                        }}>
                          {row?.participation || "10.00"}
                        </span>
                      )}
                    </Tablecell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default SwapRebalancedData;
